import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, AlertCircle, MapPin, Phone, Clock, Star, ArrowRight, Navigation } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema } from "@/components/SchemaOrg";
import motoIcon from "@/assets/icons/moto-red.webp";
import calendrierWhiteIcon from "@/assets/icons/calendrier-white.webp";
import ducatiStreetfighter from "@/assets/centre/ducati-streetfighter.webp";
import yamahaR1 from "@/assets/centre/yamaha-r1.webp";
import { cities } from "@/data/citiesData";

const echeances = [
  { periode: "Immatriculés avant 2017", date: "Depuis 2024" },
  { periode: "Immatriculés 2017-2019", date: "Depuis 2025" },
  { periode: "Immatriculés après 2020", date: "À partir de 2026" },
];

const typesL = [
  "Motos toutes cylindrées",
  "Scooters 50cc et +",
  "Mobylettes et cyclomoteurs",
  "Tricycles motorisés (3 roues)",
  "Quads et quadricycles",
  "Voiturettes sans permis (VSP)",
];

const avantages = [
  "Centre agréé catégorie L par l'État",
  "Équipement spécifique 2 roues",
  "Contrôleurs formés aux motos",
  "Résultat immédiat – PV instantané",
  "À partir de 70€",
  "Centre agréé catégorie L",
  "Ouvert le samedi matin",
  "Plus de 20 ans d'expérience",
];

const ControleTechniqueMoto93Page = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="CT Moto 93 – Dès 70€"
        description="Contrôle technique moto en Seine-Saint-Denis : 70€, obligatoire depuis 2024. Motos, scooters, quads. Centre AUTOSUR agréé à Montreuil. RDV 24/7."
        path="/controle-technique-moto-93"
      />
      <BreadcrumbSchema items={[
        { name: "Accueil", href: "/" },
        { name: "CT Moto 93", href: "/controle-technique-moto-93" }
      ]} />

      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${ducatiStreetfighter})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-autosur-red/80 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <AlertCircle className="w-4 h-4" />
              Obligatoire depuis 2024
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique Moto <span className="text-autosur-red">93</span>
            </h1>
            <p className="font-body text-white/80 text-lg mb-8">
              Centre agréé catégorie L à Montreuil-sous-Bois. Motos, scooters, quads et voiturettes. 
              À partir de 70€.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-all shadow-lg"
              >
                <img src={calendrierWhiteIcon} alt="" className="w-6 h-6" />
                Prendre RDV moto
              </Link>
              <a
                href="tel:+33149889240"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-heading font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30"
              >
                <Phone className="w-5 h-5" />
                01 49 88 92 40
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Urgence banner */}
      <section className="py-4 bg-autosur-red">
        <div className="container mx-auto px-6">
          <p className="font-heading font-semibold text-white text-center text-sm md:text-base">
            ⚠️ Le CT moto est obligatoire depuis avril 2024 – Ne tardez pas à prendre rendez-vous !
          </p>
        </div>
      </section>

      {/* Intro + Types */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Le contrôle technique moto dans le 93
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Vous cherchez un <strong className="text-autosur-navy">centre de contrôle technique moto en Seine-Saint-Denis</strong> ? 
                  Le centre <strong className="text-autosur-navy">Autosur Montreuil-sous-Bois</strong> est agréé pour le contrôle 
                  de tous les véhicules de catégorie L.
                </p>
                <p>
                  Depuis <strong className="text-autosur-navy">avril 2024</strong>, le contrôle technique est devenu obligatoire 
                  pour les motos, scooters, quads et voiturettes. Notre centre dispose de l'équipement spécifique 
                  et de contrôleurs formés pour vous accueillir.
                </p>
                <p>
                  Profitez de nos <strong className="text-autosur-navy">tarifs compétitifs à partir de 70€</strong> pour 
                  le contrôle technique de votre deux-roues.
                </p>
              </div>

              {/* Échéances */}
              <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
                <h3 className="font-heading font-bold text-autosur-navy mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Calendrier des échéances
                </h3>
                <div className="space-y-3">
                  {echeances.map((e) => (
                    <div key={e.periode} className="flex items-center justify-between">
                      <span className="font-body text-sm text-autosur-navy">{e.periode}</span>
                      <span className="font-heading font-bold text-autosur-red text-sm">{e.date}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="mt-6 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-lg">CT Moto / Catégorie L</p>
                    <p className="font-body text-muted-foreground text-sm">Toutes cylindrées</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-3xl font-bold text-autosur-red">70€</p>
                    <p className="font-body text-muted-foreground text-xs">
                      À partir de
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-4 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
                >
                  Réserver en ligne
                </Link>
                <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Offre non cumulable</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Types acceptés */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4 flex items-center gap-2">
                  <img src={motoIcon} alt="" className="w-6 h-6" />
                  Véhicules catégorie L acceptés
                </h3>
                <ul className="space-y-3">
                  {typesL.map((type) => (
                    <li key={type} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-body text-autosur-navy">{type}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={yamahaR1}
                  alt="Contrôle technique moto Yamaha R1 au centre Autosur Montreuil-sous-Bois 93"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vidéo */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Découvrez notre centre en vidéo
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Regardez comment se déroule un contrôle technique moto chez Autosur Montreuil-sous-Bois.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.youtube.com/embed/qlXcl8DbMHI"
                title="Contrôle technique moto au centre Autosur Montreuil-sous-Bois"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="w-full h-full"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Pourquoi choisir Autosur pour votre CT moto ?
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {avantages.map((avantage, index) => (
              <motion.div
                key={avantage}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="font-body text-autosur-navy text-sm">{avantage}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Avis + Localisation */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div>
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6">
                Notre centre
              </h2>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
                <a href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors">
                  <MapPin className="w-5 h-5 text-autosur-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-sm">Adresse</p>
                    <p className="font-body text-muted-foreground text-sm">133, Bd Aristide Briand, 93100 Montreuil</p>
                  </div>
                </a>
                <a href="tel:+33149889240" className="flex items-start gap-3 hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors">
                  <Phone className="w-5 h-5 text-autosur-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-sm">Téléphone</p>
                    <p className="font-body text-autosur-blue text-sm">01 49 88 92 40</p>
                  </div>
                </a>
                <a href="https://controle-technique.autosur.fr/550-autosur-montreuil-sous-bois#in-nav-rdv" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors">
                  <Clock className="w-5 h-5 text-autosur-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-sm">Horaires</p>
                    <p className="font-body text-muted-foreground text-sm">Lun-Ven: 8h30-18h30 | Sam: 9h-13h</p>
                  </div>
                </a>
                <a
                  href="https://trustville.com/avis-clients/pc/cyvO9g/controle_technique_/autosur_montreuil_sous_bois"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 pt-2 border-t border-gray-100 hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <span className="font-heading font-bold text-autosur-navy text-sm">4.9/5</span>
                  <span className="font-body text-muted-foreground text-xs">Avis clients</span>
                </a>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6">
                Accessible depuis tout le 93
              </h2>
              <div className="flex flex-wrap gap-3">
                {cities.map(city => (
                  <Link
                    key={city.slug}
                    to={`/${city.slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full font-body text-autosur-navy hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all text-sm"
                  >
                    <Navigation className="w-3 h-3 text-autosur-blue" />
                    {city.name}
                  </Link>
                ))}
              </div>
              <Link
                to="/controle-technique-pas-cher-93"
                className="mt-4 inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold hover:text-autosur-navy transition-colors text-sm"
              >
                Voir nos tarifs compétitifs dans le 93
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTASection 
        title="Votre moto a besoin d'un contrôle technique ?"
        subtitle="Prenez rendez-vous en ligne et profitez de nos tarifs préférentiels."
      />
    </SiteLayout>
  );
};

export default ControleTechniqueMoto93Page;
